import React, { useState, useRef, useEffect } from 'react';
import {
  WrapTextDropdownIcon,
  InlineWrapIcon,
  WrapTextIcon,
  BreakTextIcon,
  BehindTextIcon,
  InFrontTextIcon,
  CheckmarkIcon,
  ChevronDownIcon,
} from './editorIcons';

export const WRAP_OPTIONS = [
  {
    id: 'inline',
    label: 'In line',
    icon: InlineWrapIcon,
    description: 'Image behaves as an inline text element',
  },
  {
    id: 'wrap-text',
    label: 'Wrap text',
    icon: WrapTextIcon,
    description: 'Text flows around the image',
  },
  {
    id: 'break-text',
    label: 'Break text',
    icon: BreakTextIcon,
    description: 'Text stops above the image and resumes below it',
  },
  {
    id: 'behind-text',
    label: 'Behind text',
    icon: BehindTextIcon,
    description: 'Image sits behind the text',
  },
  {
    id: 'in-front-text',
    label: 'In front of text',
    icon: InFrontTextIcon,
    description: 'Image sits in front of the text',
  },
];

/**
 * ImageWrapDropdown
 * Matches Image 2 (button with wrap icon + chevron) and Image 3 (dropdown with 5 options + checkmark)
 */
const ImageWrapDropdown = ({
  currentWrapMode = 'inline',
  onSelectWrapMode,
  buttonClassName = '',
  dropdownClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  const activeOption = WRAP_OPTIONS.find((opt) => opt.id === currentWrapMode) || WRAP_OPTIONS[0];
  const ActiveIcon = activeOption.icon || WrapTextDropdownIcon;

  return (
    <div className="rte-gdocs-wrap-dropdown-container" ref={dropdownRef}>
      {/* Image 2: Button with wrap text icon & arrow */}
      <button
        type="button"
        className={`rte-gdocs-wrap-trigger-btn ${isOpen ? 'active' : ''} ${buttonClassName}`}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        title={`Text wrapping (${activeOption.label})`}
        aria-expanded={isOpen}
      >
        <ActiveIcon className="rte-gdocs-wrap-btn-icon" />
        <ChevronDownIcon className="rte-gdocs-wrap-btn-caret" />
      </button>

      {/* Image 3: Dropdown menu */}
      {isOpen && (
        <div className={`rte-gdocs-wrap-menu ${dropdownClassName}`} onClick={(e) => e.stopPropagation()}>
          {WRAP_OPTIONS.map((option) => {
            const OptionIcon = option.icon;
            const isSelected = currentWrapMode === option.id;

            return (
              <button
                key={option.id}
                type="button"
                className={`rte-gdocs-wrap-menu-item ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  onSelectWrapMode(option.id);
                  setIsOpen(false);
                }}
                title={option.description}
              >
                <div className="rte-gdocs-wrap-item-left">
                  <OptionIcon className="rte-gdocs-wrap-item-icon" />
                  <span className="rte-gdocs-wrap-item-label">{option.label}</span>
                </div>
                {isSelected && (
                  <CheckmarkIcon className="rte-gdocs-wrap-item-check" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ImageWrapDropdown;
